import { buttonGlyphHtml, detectPadFamily, type PadFamily } from './input-glyphs.js';
import { t } from './i18n/index.js';

/**
 * Console-style interface layer for controllers.
 *
 * - Tracks the player's *current* input device (pointer, touch, keyboard or
 *   gamepad) on `<html data-input-mode>` so every hint, prompt and focus ring
 *   follows the hands, not merely what happens to be plugged in.
 * - Drives every menu with a pad: spatial D-pad/stick focus with console
 *   key-repeat, A to confirm, B to back out, LB/RB through tab strips, left/
 *   right to adjust sliders and selectors, right stick to scroll and START to
 *   launch from the garage.
 * - Renders a contextual prompt bar with the right glyphs for the pad family.
 *
 * It stands down whenever the race owns the controller (a race stage with no
 * modal dialog on top), so driving input and menu input never overlap.
 */
export type InputMode = 'pointer' | 'touch' | 'keyboard' | 'gamepad';

export interface GamepadUiOptions {
  /** Focus moved or a value changed: a soft UI tick. */
  onNavigate?: () => void;
  /** 0..1 rumble scale for the subtle navigation tick. */
  vibration?: () => number;
  /** True while something else (e.g. a rebind capture) owns the pad. */
  suspended?: () => boolean;
}

type Direction = 'up' | 'down' | 'left' | 'right';

const FOCUSABLE = [
  'button:not([disabled])',
  'a[href]',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'summary',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

const REPEAT_DELAY_MS = 360;
const REPEAT_START_MS = 120;
const REPEAT_MIN_MS = 62;
const STICK_ENTER = 0.55;
const STICK_RELEASE = 0.32;
const MOUSE_MODE_DISTANCE = 14;

const BUTTON = { confirm: 0, back: 1, lb: 4, rb: 5, start: 9, up: 12, down: 13, left: 14, right: 15 } as const;

export class GamepadUi {
  private frame = 0;
  private started = false;
  private modeValue: InputMode = 'pointer';
  private familyValue: PadFamily = 'xbox';
  private padIndex: number | null = null;
  private previousButtons: boolean[] = [];
  private heldDirection: Direction | null = null;
  private nextRepeatAt = 0;
  private repeatCount = 0;
  private stickDirection: Direction | null = null;
  private mouseTravel = 0;
  private lastMouse: { x: number; y: number } | null = null;
  private promptKey = '';
  private promptRefreshAt = 0;
  private promptBar: HTMLElement | null = null;
  private lastScope: HTMLElement | null = null;
  private nextFocusCheckAt = 0;
  private wasSuspended = false;
  /** Set when a rebind capture ends: menu input waits for a neutral pad. */
  private waitForNeutral = false;
  private readonly listeners = new Set<(mode: InputMode, family: PadFamily) => void>();

  constructor(private readonly options: GamepadUiOptions = {}) {}

  get mode(): InputMode {
    return this.modeValue;
  }

  get family(): PadFamily {
    return this.familyValue;
  }

  start(): void {
    if (this.started) return;
    this.started = true;
    const coarse = typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches;
    this.setMode(coarse ? 'touch' : 'pointer');
    window.addEventListener('keydown', this.onKeyDown, true);
    window.addEventListener('pointerdown', this.onPointerDown, true);
    window.addEventListener('mousemove', this.onMouseMove, { passive: true });
    window.addEventListener('gamepadconnected', this.onGamepadConnected);
    document.addEventListener('focusin', this.onFocusIn);
    this.frame = requestAnimationFrame(this.tick);
  }

  destroy(): void {
    if (!this.started) return;
    this.started = false;
    cancelAnimationFrame(this.frame);
    window.removeEventListener('keydown', this.onKeyDown, true);
    window.removeEventListener('pointerdown', this.onPointerDown, true);
    window.removeEventListener('mousemove', this.onMouseMove);
    window.removeEventListener('gamepadconnected', this.onGamepadConnected);
    document.removeEventListener('focusin', this.onFocusIn);
    this.promptBar?.remove();
    this.promptBar = null;
  }

  onChange(listener: (mode: InputMode, family: PadFamily) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /** Forces the prompt bar to re-evaluate, e.g. after a dialog opened. */
  refresh(): void {
    this.promptKey = '';
    this.promptRefreshAt = 0;
  }

  private setMode(mode: InputMode, family = this.familyValue): void {
    if (mode === this.modeValue && family === this.familyValue) return;
    this.modeValue = mode;
    this.familyValue = family;
    const root = document.documentElement;
    root.dataset.inputMode = mode;
    root.dataset.padFamily = family;
    this.refresh();
    for (const listener of this.listeners) listener(mode, family);
  }

  private readonly onKeyDown = (event: KeyboardEvent): void => {
    if (['ShiftLeft', 'ShiftRight', 'ControlLeft', 'ControlRight', 'AltLeft', 'AltRight', 'MetaLeft', 'MetaRight'].includes(event.code)
      && this.modeValue === 'keyboard') return;
    this.setMode('keyboard');
  };

  private readonly onPointerDown = (event: PointerEvent): void => {
    this.mouseTravel = 0;
    this.setMode(event.pointerType === 'touch' || event.pointerType === 'pen' ? 'touch' : 'pointer');
  };

  private readonly onMouseMove = (event: MouseEvent): void => {
    if (this.modeValue === 'pointer') return;
    // Ignore sub-pixel jitter and synthetic moves so a resting mouse does not
    // steal prompts away from the controller.
    if (this.lastMouse) this.mouseTravel += Math.abs(event.clientX - this.lastMouse.x) + Math.abs(event.clientY - this.lastMouse.y);
    this.lastMouse = { x: event.clientX, y: event.clientY };
    if (this.mouseTravel > MOUSE_MODE_DISTANCE && this.modeValue !== 'touch') {
      this.mouseTravel = 0;
      this.setMode('pointer');
    }
  };

  private readonly onGamepadConnected = (event: Event): void => {
    const pad = (event as GamepadEvent).gamepad;
    this.familyValue = detectPadFamily(pad.id);
    document.documentElement.dataset.padFamily = this.familyValue;
    this.refresh();
  };

  private readonly onFocusIn = (event: FocusEvent): void => {
    this.promptRefreshAt = 0;
    if (event.target instanceof HTMLElement) markPadFocus(event.target);
  };

  private readonly tick = (now: number): void => {
    if (!this.started) return;
    this.frame = requestAnimationFrame(this.tick);
    if (document.hidden) return;
    const pad = this.readPad();
    if (!pad) {
      this.previousButtons = [];
      this.heldDirection = null;
      this.syncPromptBar(now, null);
      return;
    }
    const pressed = pad.buttons.map((button) => Boolean(button?.pressed) || Number(button?.value) > 0.5);
    const active = pressed.some(Boolean) || standardStickActive(pad);
    if (active && this.modeValue !== 'gamepad') {
      this.setMode('gamepad', detectPadFamily(pad.id));
      // With nothing focused yet the waking press only reveals the focus
      // ring; with a control already focused it acts immediately.
      const wakeScope = this.activeScope();
      if (!wakeScope || !this.focusedIn(wakeScope)) {
        this.previousButtons = pressed;
        this.ensureFocus(wakeScope);
        return;
      }
    }
    if (active && detectPadFamily(pad.id) !== this.familyValue) this.setMode('gamepad', detectPadFamily(pad.id));

    const suspended = Boolean(this.options.suspended?.());
    // The capture may resolve from the race input loop before this loop ever
    // sees the bound button go down; never replay it as a menu press.
    if (this.wasSuspended && !suspended) this.waitForNeutral = true;
    this.wasSuspended = suspended;
    if (this.waitForNeutral && !active) this.waitForNeutral = false;
    const scope = suspended || this.waitForNeutral ? null : this.activeScope();
    // A dialog that opens under controller play takes focus on its default
    // action at once, the way console menus do.
    if (scope !== this.lastScope || (scope && now >= this.nextFocusCheckAt)) {
      this.lastScope = scope;
      this.nextFocusCheckAt = now + 200;
      // Also covers a default action that was disabled when the dialog
      // opened (START while the track still loads).
      if (scope && this.modeValue === 'gamepad') this.ensureFocus(scope);
    }
    if (scope) this.handleNavigation(pad, pressed, scope, now);
    else {
      this.heldDirection = null;
      this.stickDirection = null;
    }
    this.previousButtons = pressed;
    this.syncPromptBar(now, scope);
  };

  private readPad(): Gamepad | null {
    if (typeof navigator.getGamepads !== 'function') return null;
    let pads: readonly (Gamepad | null)[];
    try {
      pads = navigator.getGamepads();
    } catch {
      return null;
    }
    const current = this.padIndex === null ? null : pads[this.padIndex];
    if (current?.connected) {
      // Hand over to another pad the moment it is used.
      const other = pads.find((pad) => pad?.connected && pad.index !== current.index
        && pad.buttons.some((button) => button?.pressed));
      if (other) {
        this.padIndex = other.index;
        this.previousButtons = other.buttons.map((button) => Boolean(button?.pressed));
        return other;
      }
      return current;
    }
    const next = pads.find((pad) => pad?.connected && pad.buttons.some((button) => button?.pressed))
      ?? pads.find((pad) => pad?.connected) ?? null;
    this.padIndex = next?.index ?? null;
    return next;
  }

  /** The topmost open modal, the menu when no race is mounted, else null (driving). */
  private activeScope(): HTMLElement | null {
    const dialogs = [...document.querySelectorAll<HTMLElement>('[role="dialog"][aria-modal="true"]')].filter(isShown);
    if (dialogs.length > 0) return dialogs[dialogs.length - 1]!;
    if (document.getElementById('race-stage')) return null;
    return document.querySelector<HTMLElement>('main') ?? document.body;
  }

  private handleNavigation(pad: Gamepad, pressed: boolean[], scope: HTMLElement, now: number): void {
    const edge = (index: number): boolean => Boolean(pressed[index]) && !this.previousButtons[index];

    // Direction from the D-pad or left stick, with hysteresis on the stick.
    let direction: Direction | null = pressed[BUTTON.up] ? 'up'
      : pressed[BUTTON.down] ? 'down'
        : pressed[BUTTON.left] ? 'left'
          : pressed[BUTTON.right] ? 'right' : null;
    if (!direction && pad.mapping === 'standard') {
      const x = Number(pad.axes[0] ?? 0);
      const y = Number(pad.axes[1] ?? 0);
      const magnitude = Math.max(Math.abs(x), Math.abs(y));
      if (this.stickDirection && magnitude > STICK_RELEASE) direction = this.stickDirection;
      else if (magnitude > STICK_ENTER) direction = Math.abs(x) > Math.abs(y) ? x > 0 ? 'right' : 'left' : y > 0 ? 'down' : 'up';
      this.stickDirection = direction;
    }

    if (direction !== this.heldDirection) {
      this.heldDirection = direction;
      this.repeatCount = 0;
      this.nextRepeatAt = now + REPEAT_DELAY_MS;
      if (direction) this.navigate(direction, scope, false);
    } else if (direction && now >= this.nextRepeatAt) {
      this.repeatCount += 1;
      this.nextRepeatAt = now + Math.max(REPEAT_MIN_MS, REPEAT_START_MS - this.repeatCount * 9);
      this.navigate(direction, scope, true);
    }

    if (edge(BUTTON.confirm)) this.confirm(scope);
    if (edge(BUTTON.back)) this.back(scope);
    if (edge(BUTTON.lb)) this.cycleTabs(scope, -1);
    if (edge(BUTTON.rb)) this.cycleTabs(scope, 1);
    if (edge(BUTTON.start) && !document.getElementById('race-stage')) {
      const start = visibleIn(scope, '[data-pad-start]');
      if (start) {
        start.focus({ preventScroll: true });
        start.click();
      }
    }

    // Right stick scrolls long panels without moving focus.
    if (pad.mapping === 'standard') {
      const scroll = applyDeadZone(Number(pad.axes[3] ?? 0), 0.2);
      if (scroll !== 0) {
        const target = scrollableAncestor(document.activeElement instanceof HTMLElement && scope.contains(document.activeElement)
          ? document.activeElement
          : scope) ?? scope;
        target.scrollBy({ top: scroll * Math.abs(scroll) * 26 });
      }
    }
  }

  private navigate(direction: Direction, scope: HTMLElement, repeat: boolean): void {
    const current = this.focusedIn(scope);
    if (!current) {
      this.ensureFocus(scope);
      return;
    }
    if (direction === 'left' || direction === 'right') {
      const step = direction === 'right' ? 1 : -1;
      if (current instanceof HTMLInputElement && current.type === 'range') {
        adjustRange(current, step, repeat ? this.repeatCount : 0);
        this.feedback();
        return;
      }
      if (current instanceof HTMLSelectElement) {
        cycleSelect(current, step);
        this.feedback();
        return;
      }
      if (current instanceof HTMLInputElement && current.type === 'radio') {
        const next = neighbourRadio(current, step);
        if (next) {
          next.focus({ preventScroll: true });
          next.click();
          this.feedback();
          return;
        }
      }
    }
    const next = spatialNeighbour(current, direction, focusablesIn(scope));
    if (!next) return;
    focusElement(next);
    this.feedback();
  }

  private confirm(scope: HTMLElement): void {
    const current = this.focusedIn(scope);
    if (!current) {
      this.ensureFocus(scope);
      return;
    }
    if (current instanceof HTMLSelectElement) {
      cycleSelect(current, 1);
      this.feedback();
      return;
    }
    if (current instanceof HTMLInputElement && (current.type === 'range' || current.type === 'text' || current.type === 'search')) return;
    current.click();
  }

  private back(scope: HTMLElement): void {
    const active = document.activeElement;
    const details = active instanceof HTMLElement ? active.closest('details[open]') : null;
    if (details instanceof HTMLDetailsElement && scope.contains(details)) {
      details.open = false;
      details.querySelector<HTMLElement>('summary')?.focus();
      this.feedback();
      return;
    }
    const back = visibleIn(scope, '[data-pad-back]');
    if (back) back.click();
  }

  private cycleTabs(scope: HTMLElement, step: number): void {
    const strip = [...scope.querySelectorAll<HTMLElement>('[data-pad-tabs]')].find(isShown);
    if (!strip) return;
    const tabs = [...strip.querySelectorAll<HTMLElement>('button:not([disabled])')].filter(isShown);
    if (tabs.length < 2) return;
    let index = tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true'
      || tab.getAttribute('aria-current') === 'true' || tab.getAttribute('aria-current') === 'step'
      || tab.classList.contains('active'));
    if (index < 0) index = 0;
    const next = tabs[(index + step + tabs.length) % tabs.length]!;
    next.click();
    focusElement(next);
    this.feedback();
  }

  private ensureFocus(scope: HTMLElement | null): void {
    if (!scope || this.focusedIn(scope)) return;
    const preferred = [...scope.querySelectorAll<HTMLElement>('[data-pad-default]')].find((element) => isFocusable(element));
    const target = preferred ?? focusablesIn(scope)[0];
    if (target) {
      focusElement(target);
      this.feedback();
    }
  }

  private focusedIn(scope: HTMLElement): HTMLElement | null {
    const active = document.activeElement;
    const focused = active instanceof HTMLElement && active !== document.body && scope.contains(active) && isFocusable(active)
      ? active
      : null;
    if (focused !== padFocused) markPadFocus(focused);
    return focused;
  }

  private feedback(): void {
    this.options.onNavigate?.();
    this.promptRefreshAt = 0;
    const scale = this.options.vibration?.() ?? 1;
    if (scale <= 0 || this.padIndex === null) return;
    try {
      const pad = navigator.getGamepads()[this.padIndex];
      const actuator = pad?.vibrationActuator;
      void actuator?.playEffect?.('dual-rumble', {
        duration: 16, startDelay: 0, strongMagnitude: 0, weakMagnitude: 0.12 * scale,
      })?.catch(() => undefined);
    } catch {
      // Navigation never depends on haptics.
    }
  }

  private syncPromptBar(now: number, scope: HTMLElement | null): void {
    if (now < this.promptRefreshAt) return;
    this.promptRefreshAt = now + 250;
    const visible = this.modeValue === 'gamepad' && scope !== null && !this.options.suspended?.();
    const html = visible && scope ? this.promptHtml(scope) : '';
    const key = `${visible}:${html}`;
    if (key === this.promptKey) return;
    this.promptKey = key;
    if (!this.promptBar) {
      this.promptBar = document.createElement('div');
      this.promptBar.className = 'pad-prompts';
      this.promptBar.setAttribute('aria-hidden', 'true');
      document.body.append(this.promptBar);
    }
    this.promptBar.innerHTML = html;
    this.promptBar.classList.toggle('visible', visible);
  }

  private promptHtml(scope: HTMLElement): string {
    const family = this.familyValue;
    const items: string[] = [];
    const focused = this.focusedIn(scope);
    const adjustable = focused instanceof HTMLSelectElement
      || (focused instanceof HTMLInputElement && focused.type === 'range');
    if (adjustable) items.push(prompt(`${buttonGlyphHtml(14, family)}${buttonGlyphHtml(15, family)}`, t('ADJUST')));
    if (!(focused instanceof HTMLInputElement && focused.type === 'range')) {
      const verb = focused instanceof HTMLInputElement && (focused.type === 'checkbox' || focused.type === 'radio')
        ? t('TOGGLE')
        : focused?.dataset.padVerb ?? t('SELECT');
      items.push(prompt(buttonGlyphHtml(BUTTON.confirm, family), verb));
    }
    const inDetails = focused?.closest('details[open]');
    const back = visibleIn(scope, '[data-pad-back]');
    if (inDetails || back) items.push(prompt(buttonGlyphHtml(BUTTON.back, family), inDetails ? t('CLOSE') : back?.dataset.padBackLabel ?? t('BACK')));
    if ([...scope.querySelectorAll<HTMLElement>('[data-pad-tabs]')].some(isShown)) {
      items.push(prompt(`${buttonGlyphHtml(BUTTON.lb, family)}${buttonGlyphHtml(BUTTON.rb, family)}`, t('SECTION')));
    }
    if (!document.getElementById('race-stage') && visibleIn(scope, '[data-pad-start]')) {
      items.push(prompt(buttonGlyphHtml(BUTTON.start, family), t('RACE')));
    }
    return items.join('');
  }
}

function prompt(glyphs: string, label: string): string {
  return `<span class="pad-prompt">${glyphs}<b>${label}</b></span>`;
}

function standardStickActive(pad: Gamepad): boolean {
  if (pad.mapping !== 'standard') return false;
  return pad.axes.slice(0, 4).some((value) => Math.abs(Number(value)) > 0.5);
}

function applyDeadZone(value: number, deadZone: number): number {
  if (!Number.isFinite(value) || Math.abs(value) <= deadZone) return 0;
  return Math.sign(value) * (Math.abs(value) - deadZone) / (1 - deadZone);
}

function isShown(element: HTMLElement): boolean {
  if (element.hidden || element.inert || element.classList.contains('hidden')) return false;
  if (element.closest('[inert], [hidden], [aria-hidden="true"]')) return false;
  const rect = element.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) return false;
  const style = getComputedStyle(element);
  return style.visibility !== 'hidden' && style.display !== 'none' && Number(style.opacity) > 0.05;
}

function navTarget(element: HTMLElement): HTMLElement {
  // Visually hidden checkboxes/radios live inside a styled label.
  if (element instanceof HTMLInputElement && (element.type === 'checkbox' || element.type === 'radio')) {
    return element.closest('label') ?? element.parentElement ?? element;
  }
  return element;
}

function isFocusable(element: HTMLElement): boolean {
  if (!element.matches(FOCUSABLE)) return false;
  const target = navTarget(element);
  if (!isShown(target)) return false;
  if (target !== element && element.closest('[inert], [hidden]')) return false;
  return true;
}

function focusablesIn(scope: HTMLElement): HTMLElement[] {
  return [...scope.querySelectorAll<HTMLElement>(FOCUSABLE)].filter((element) => {
    if (element.closest('details:not([open])') && element.tagName !== 'SUMMARY') return false;
    return isFocusable(element);
  });
}

function visibleIn(scope: HTMLElement, selector: string): HTMLElement | null {
  return [...scope.querySelectorAll<HTMLElement>(selector)].find((element) => isShown(element) && !(element as HTMLButtonElement).disabled) ?? null;
}

let padFocused: HTMLElement | null = null;

/**
 * Mirrors focus into `data-pad-focus` so the ring renders even when the
 * browser window itself is not OS-focused (where `:focus` stops matching).
 */
function markPadFocus(element: HTMLElement | null): void {
  if (padFocused === element) return;
  padFocused?.removeAttribute('data-pad-focus');
  padFocused = element;
  element?.setAttribute('data-pad-focus', '');
}

function focusElement(element: HTMLElement): void {
  element.focus({ preventScroll: true });
  markPadFocus(element);
  navTarget(element).scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
}

/**
 * Picks the nearest candidate in a direction. Candidates that overlap the
 * current element on the cross axis win over diagonal ones, so moving down a
 * column never jumps sideways while a same-column target exists.
 */
export function spatialNeighbour(current: HTMLElement, direction: Direction, candidates: readonly HTMLElement[]): HTMLElement | null {
  const from = navTarget(current).getBoundingClientRect();
  let best: HTMLElement | null = null;
  let bestScore = Number.POSITIVE_INFINITY;
  const vertical = direction === 'up' || direction === 'down';
  const fromCentre = vertical ? (from.top + from.bottom) / 2 : (from.left + from.right) / 2;
  for (const candidate of candidates) {
    if (candidate === current) continue;
    const target = navTarget(candidate);
    if (target === navTarget(current)) continue;
    const rect = target.getBoundingClientRect();
    const centre = vertical ? (rect.top + rect.bottom) / 2 : (rect.left + rect.right) / 2;
    const forward = direction === 'down' || direction === 'right' ? centre - fromCentre : fromCentre - centre;
    if (forward <= 4) continue;
    const gap = direction === 'down' ? rect.top - from.bottom
      : direction === 'up' ? from.top - rect.bottom
        : direction === 'right' ? rect.left - from.right
          : from.left - rect.right;
    // Allow slight overlap on the travel axis (tight grids, focus rings).
    if (gap < -Math.min(vertical ? from.height : from.width, vertical ? rect.height : rect.width) * 0.5) continue;
    const overlap = vertical
      ? Math.min(from.right, rect.right) - Math.max(from.left, rect.left)
      : Math.min(from.bottom, rect.bottom) - Math.max(from.top, rect.top);
    const crossDistance = overlap > 0
      ? 0
      : vertical
        ? Math.min(Math.abs(rect.left - from.right), Math.abs(from.left - rect.right))
        : Math.min(Math.abs(rect.top - from.bottom), Math.abs(from.top - rect.bottom));
    // Stay inside a forward cone: the end of a tab row must not dive into
    // a footer button far below it.
    if (overlap <= 0 && crossDistance > forward) continue;
    const crossCentre = vertical
      ? Math.abs((rect.left + rect.right) / 2 - (from.left + from.right) / 2)
      : Math.abs((rect.top + rect.bottom) / 2 - (from.top + from.bottom) / 2);
    const score = Math.max(0, gap) + crossDistance * 2.4 + crossCentre * 0.18 + (overlap > 0 ? 0 : 40);
    if (score < bestScore) {
      bestScore = score;
      best = candidate;
    }
  }
  return best;
}

function adjustRange(input: HTMLInputElement, direction: number, repeatCount: number): void {
  const min = Number(input.min || 0);
  const max = Number(input.max || 100);
  const step = Number(input.step) || (max - min) / 20;
  const multiplier = repeatCount > 8 ? 3 : 1;
  const value = Math.max(min, Math.min(max, Number(input.value) + direction * step * multiplier));
  const rounded = Math.round(value / step) * step;
  const next = String(Number(Math.max(min, Math.min(max, rounded)).toFixed(6)));
  if (next === input.value) return;
  input.value = next;
  input.dispatchEvent(new Event('input', { bubbles: true }));
  input.dispatchEvent(new Event('change', { bubbles: true }));
}

function cycleSelect(select: HTMLSelectElement, direction: number): void {
  const options = [...select.options].filter((option) => !option.disabled);
  if (options.length < 2) return;
  const index = options.findIndex((option) => option.value === select.value);
  const next = options[(index + direction + options.length) % options.length]!;
  select.value = next.value;
  select.dispatchEvent(new Event('input', { bubbles: true }));
  select.dispatchEvent(new Event('change', { bubbles: true }));
}

function neighbourRadio(radio: HTMLInputElement, direction: number): HTMLInputElement | null {
  if (!radio.name) return null;
  const group = [...document.querySelectorAll<HTMLInputElement>(`input[type="radio"][name="${CSS.escape(radio.name)}"]`)]
    .filter((candidate) => !candidate.disabled && isFocusable(candidate));
  const index = group.indexOf(radio);
  if (index < 0) return null;
  return group[index + direction] ?? null;
}

function scrollableAncestor(element: HTMLElement): HTMLElement | null {
  let node: HTMLElement | null = element;
  while (node) {
    const style = getComputedStyle(node);
    if (/(auto|scroll)/.test(style.overflowY) && node.scrollHeight > node.clientHeight + 4) return node;
    node = node.parentElement;
  }
  return document.scrollingElement instanceof HTMLElement ? document.scrollingElement : null;
}
